/**
 * Ingreso / Alta manual de series.
 *
 * El operador crea un batch de series nuevas para un GTIN+Lote dado en su
 * ubicación activa. Las series quedan `altaManual: true` + en_poder. NO se
 * informan al SNT — el SNT se entera al despacharlas.
 *
 * Modo simplificado (Fase 3.C): solo "Por rango" con prefijo + desde/hasta.
 * Los modos lista y archivo se pueden agregar en una iteración futura.
 */

import { useState, useMemo } from 'react';
import { Loader2, Plus, AlertCircle, Tag } from 'lucide-react';
import { toast } from '@/lib/notify';
import { useProductos, useCrearIngresoMutation } from '@/api/queries';
import { EtiquetasGs1Modal } from '@/features/etiquetas/EtiquetasGs1Modal';
import type { Serie } from '@/api/contracts/series';
import { useSessionStore } from '@/stores/session';
import { useAuditStore } from '@/stores/audit';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fmtNum } from '@/lib/format';
import { validarVencimientoMmYyyy } from '@/domain/gs1';
import { puedeActuar } from '@/domain/rbac';
import { Lock } from 'lucide-react';

export function IngresoPage() {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const usuario = useSessionStore((s) => s.usuario);
  const addAuditEntry = useAuditStore((s) => s.addEntry);

  const { data: productos = [] } = useProductos();
  const mutation = useCrearIngresoMutation();

  const [gtin, setGtin] = useState('');
  const [lote, setLote] = useState('');
  const [vencimiento, setVencimiento] = useState('');
  const [prefijo, setPrefijo] = useState('SN-2026-');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [razon, setRazon] = useState('');
  // Tras crear un ingreso, abrimos automáticamente el modal de etiquetas
  // con las series recién creadas (paridad mockup v10).
  const [etiquetasOpen, setEtiquetasOpen] = useState(false);
  const [seriesCreadas, setSeriesCreadas] = useState<Serie[]>([]);
  const [ingresoIdCreado, setIngresoIdCreado] = useState<string>('');

  const { seriesIds, error: errorCalc } = useMemo<{ seriesIds: string[]; error: string | null }>(() => {
    if (!prefijo.trim()) return { seriesIds: [], error: 'Prefijo requerido' };
    const d = parseInt(desde, 10);
    const h = parseInt(hasta, 10);
    if (!desde || !hasta || isNaN(d) || isNaN(h)) return { seriesIds: [], error: null };
    if (h < d) return { seriesIds: [], error: '"Hasta" debe ser ≥ "Desde"' };
    const cant = h - d + 1;
    if (cant > 10000) return { seriesIds: [], error: 'Máximo 10.000 series por batch' };
    const padLen = Math.max(7, String(h).length);
    const ids: string[] = [];
    for (let i = d; i <= h; i++) ids.push(prefijo + String(i).padStart(padLen, '0'));
    return { seriesIds: ids, error: null };
  }, [prefijo, desde, hasta]);

  const canSubmit =
    !!gtin &&
    !!lote.trim() &&
    !!vencimiento &&
    validarVencimientoMmYyyy(vencimiento) &&
    seriesIds.length > 0 &&
    !errorCalc &&
    !mutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // RBAC defensivo (paquete L.1): solo Admin puede crear ingresos manuales.
    // El menú está oculto al Operador, pero esto previene acceso vía URL directa.
    if (!puedeActuar(usuario, 'crear_ingreso_manual')) {
      addAuditEntry({
        categoria: 'sesion',
        accion: 'permiso.denegado',
        detalle: 'Intento de alta manual de series sin permiso (requiere crear_ingreso_manual)',
      });
      toast.error('Sin permiso para crear series · feature solo para Administrador');
      return;
    }
    if (!canSubmit || !usuario || !ubicacionActivaId) return;
    mutation.mutate(
      {
        gtin,
        lote: lote.trim(),
        vencimiento,
        ubicacionId: ubicacionActivaId,
        usuarioId: usuario.id,
        seriesIds,
        razon: razon.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          addAuditEntry({
            categoria: 'operacion_critica',
            accion: 'serie.alta_manual',
            entidadTipo: 'ingreso',
            entidadId: data.ingresoId,
            valoresDespues: {
              gtin,
              lote: lote.trim(),
              vencimiento,
              ubicacionId: ubicacionActivaId,
              cantidad: data.series.length,
              primera: data.series[0]?.id,
              ultima: data.series[data.series.length - 1]?.id,
            },
            razon: razon.trim() || `Alta manual de ${data.series.length} series · Lote ${lote.trim()}`,
          });
          toast.success(`Ingreso ${data.ingresoId} creado · ${data.series.length} series`);
          // Auto-abrir modal de etiquetas GS1 con las series creadas
          setSeriesCreadas(data.series);
          setIngresoIdCreado(data.ingresoId);
          setEtiquetasOpen(true);
          // Limpiar formulario
          setLote('');
          setVencimiento('');
          setDesde('');
          setHasta('');
          setRazon('');
        },
        onError: (err) => toast.error(`Error: ${err.message}`),
      },
    );
  };

  const productoSeleccionado = productos.find((p) => p.gtin === gtin) ?? null;
  // Para usar como producto en el modal post-ingreso (necesita serie+producto en sync)
  const primeraSerieCreada = seriesCreadas[0];
  const productoCreado = primeraSerieCreada
    ? productos.find((p) => p.gtin === primeraSerieCreada.gtin) ?? null
    : null;

  // Guard de página — Operador no tiene acceso (paquete L.1). El sidebar oculta
  // el item pero el usuario podría llegar vía URL directa.
  if (!puedeActuar(usuario, 'crear_ingreso_manual')) {
    return (
      <Card>
        <CardContent className="space-y-3 py-12 text-center">
          <Lock className="mx-auto h-8 w-8 text-gray-600" />
          <div className="text-sm font-semibold text-gray-700">
            Acceso restringido
          </div>
          <div className="mx-auto max-w-md text-xs text-gray-600">
            El alta manual de series está limitada al rol <b>Administrador</b>. Si
            necesitás cargar series, contactá a un administrador del sistema.
          </div>
        </CardContent>
      </Card>
    );
  }

  // Banner v2 — feature prevista para post-MVP. Visible para Admin (que sí ve
  // la pantalla) para indicar que el backend real no se implementará en v1.
  // El UI funciona contra el mock y permite validar UX, pero el equipo de
  // backend no debe priorizar esta feature ahora (Paquete P).

  return (
    <div className="space-y-6">
      <Alert className="border-amber-300 bg-amber-50">
        <AlertCircle className="h-4 w-4 text-amber-700" />
        <AlertDescription className="text-xs text-amber-900">
          <b>Feature prevista para v2 (post-MVP).</b> El alta manual de series es funcional
          contra el mock para validar UX, pero el backend real <b>no se implementa en v1</b>.
          Las series creadas acá no se persisten al desconectar el mock — ver Handoff técnico
          para alcance v1 vs v2.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-everex-azul">
            <Plus className="h-5 w-5" />
            Ingreso / Alta manual de series
            <span className="ml-2 rounded bg-amber-200 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-800">
              v2
            </span>
          </CardTitle>
          <p className="text-xs text-gray-600">
            Carga inicial de series para un GTIN+Lote en tu ubicación activa. Las series
            quedan registradas bajo tu tenencia pero <b>NO se informan al SNT</b> hasta que se despachen.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="md:col-span-1">
                <Label className="mb-1 block text-xs">Producto (GTIN)</Label>
                <Select value={gtin} onValueChange={setGtin}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {productos.map((p) => (
                      <SelectItem key={p.gtin} value={p.gtin}>
                        {p.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Lote</Label>
                <Input value={lote} onChange={(e) => setLote(e.target.value)} placeholder="BIR-2026X" />
              </div>
              <div>
                <Label className="mb-1 block text-xs">Vencimiento (MM/YYYY)</Label>
                <Input
                  value={vencimiento}
                  onChange={(e) => setVencimiento(e.target.value)}
                  placeholder="12/2028"
                  className={!vencimiento || validarVencimientoMmYyyy(vencimiento) ? '' : 'border-red-400'}
                />
              </div>
            </div>

            <div className="rounded border border-gray-200 bg-gray-50 p-4">
              <div className="mb-2 text-xs font-semibold uppercase text-gray-600">
                Rango de series
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <Label className="mb-1 block text-xs">Prefijo</Label>
                  <Input
                    value={prefijo}
                    onChange={(e) => setPrefijo(e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Desde</Label>
                  <Input
                    type="number"
                    value={desde}
                    onChange={(e) => setDesde(e.target.value)}
                    placeholder="1"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Hasta</Label>
                  <Input
                    type="number"
                    value={hasta}
                    onChange={(e) => setHasta(e.target.value)}
                    placeholder="100"
                  />
                </div>
              </div>
              {errorCalc && (
                <Alert variant="destructive" className="mt-3">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">{errorCalc}</AlertDescription>
                </Alert>
              )}
              {seriesIds.length > 0 && (
                <div className="mt-3 rounded bg-white p-3 text-xs">
                  <div className="text-everex-azul">
                    Se crearán <b>{fmtNum(seriesIds.length)}</b> series:
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <code className="rounded bg-everex-azul-bg px-1.5 py-0.5 font-mono text-[11px]">
                      {seriesIds[0]}
                    </code>
                    <span className="text-gray-600">→</span>
                    <code className="rounded bg-everex-azul-bg px-1.5 py-0.5 font-mono text-[11px]">
                      {seriesIds[seriesIds.length - 1]}
                    </code>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="mb-1 block text-xs">
                Razón / observaciones <span className="text-gray-600">(opcional)</span>
              </Label>
              <Input
                value={razon}
                onChange={(e) => setRazon(e.target.value)}
                placeholder="Ej: carga inicial migrada desde sistema viejo"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="submit"
                disabled={!canSubmit}
                className="bg-everex-naranja text-white hover:bg-everex-naranja-hover disabled:bg-gray-300"
                size="lg"
              >
                {mutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creando...</>
                ) : (
                  <><Tag className="mr-2 h-4 w-4" /> Crear {fmtNum(seriesIds.length)} series</>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Modal de etiquetas GS1 — abre automáticamente tras crear ingreso */}
      <EtiquetasGs1Modal
        open={etiquetasOpen}
        onClose={() => setEtiquetasOpen(false)}
        producto={productoCreado ?? productoSeleccionado}
        series={seriesCreadas}
        contexto={
          ingresoIdCreado
            ? `Lote ${ingresoIdCreado} · ${seriesCreadas.length} series · DataMatrix GS1`
            : undefined
        }
      />
    </div>
  );
}
